import { useEffect, useState, useMemo, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { getEvents, deleteEvent } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { I18nContext } from "../context/I18nContext";

export default function Events() {
  const { languageCode } = useContext(I18nContext);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // أيقونات بأسماء PascalCase لتجنب الـ Warnings
  const ChevronLeft = ICONS.ChevronLeft;
  //const ActivityIcon = ICONS.Groups; // أو أيقونة تعبر عن النشاط
  const PlusIcon = ICONS.Plus;
  const EditIcon = ICONS.EditUser;
  const DeleteIcon = ICONS.DeleteUser;
  const BellAlert = ICONS.BellAlert;

  // استخدام useCallback لمنع إعادة التعريف غير الضرورية وحل تحذير useEffect
  // languageCode is included here (both in the call and the dependency
  // array below) so switching the LanguagePicker while already sitting on
  // this page refetches immediately with a translated event_name, instead
  // of only picking it up on the next full page load.
  const fetchEvents = useCallback(async () => {
    try {
      const data = await getEvents(token, { lang: languageCode });
      setEvents(data || []);
    } catch (err) {
      console.error(err);
      notify.error("Failed to load events");
    }
  }, [token, languageCode]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // منطق البحث والفرز
  const sortedAndFilteredEvents = useMemo(() => {
    let result = [...events].filter((e) =>
      e.event_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.route_path.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [events, searchTerm, sortConfig]);

  const currentEvents = sortedAndFilteredEvents.slice(
    (currentPage - 1) * eventsPerPage,
    currentPage * eventsPerPage
  );
  const totalPages = Math.ceil(sortedAndFilteredEvents.length / eventsPerPage) || 1;
  const totalItems = sortedAndFilteredEvents.length;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => {
    setModalConfig({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    try {
      await deleteEvent(token, modalConfig.id);
      notify.success("Event configuration deleted successfully");
      fetchEvents();
    } catch (err) {
      notify.error(err.message || "Failed to delete event");
    } finally {
      setModalConfig({ isOpen: false, id: null });
    }
  };

  // تعريف الأعمدة
  const columns = [
    { key: "id", label: "ID", className: "text-gray-400 font-mono text-[10px]" },
    {
      key: "event_name",
      label: "Event Name",
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-black text-gray-800 text-sm tracking-tight">{row.event_name}</span>
          <span className="text-[10px] text-gray-400 italic font-medium">{row.description || "No description"}</span>
        </div>
      ),
    },
    {
      key: "route_path",
      label: "Endpoint",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[9px] font-black border 
            ${row.method === 'POST' ? 'bg-green-50 border-green-100 text-green-600' : 
              row.method === 'DELETE' ? 'bg-red-50 border-red-100 text-red-600' : 
              'bg-blue-50 border-blue-100 text-blue-600'}`}>
            {row.method}
          </span>
          <code className="text-[11px] text-gray-500 font-mono">{row.route_path}</code>
        </div>
      ),
    },
    {
      key: "is_enabled",
      label: "Status",
      render: (row) => (
        <div className={`flex items-center gap-1.5 text-[10px] font-bold ${row.is_enabled ? 'text-green-500' : 'text-gray-300'}`}>
          <div className={`w-2 h-2 rounded-full ${row.is_enabled ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-gray-300'}`} />
          {row.is_enabled ? "ACTIVE" : "DISABLED"}
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title="Delete Event Rule"
        message="This will stop tracking this activity. Are you sure?"
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
      />

      {/* Header Section */}
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="text-[var(--primary-color)]">
            <BellAlert size={40} className="w-10 h-10 shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-black tracking-tight" style={{ color: "var(--primary-color)" }}>Event Configs</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium italic">Define and monitor system activities</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder="Search events or routes..."
          />
          <Button variant="primary" icon={PlusIcon} onClick={() => navigate("/add-event")} className="w-full sm:w-auto shrink-0 shadow-lg shadow-blue-500/20">
            Add Event
          </Button>
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        data={currentEvents}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={EditIcon} onClick={() => navigate(`/edit-event/${row.id}`)}>
              {isMobile && " Edit"}
            </Button>
            <Button variant="danger" size="sm" icon={DeleteIcon} onClick={() => handleDeleteClick(row.id)}>
              {isMobile && " Delete"}
            </Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-400 font-mono">#{row.id}</span>
              <h3 className="font-bold text-gray-800">{row.event_name}</h3>
            </div>
            <span className="text-[10px] font-black text-blue-600">{row.method}</span>
          </div>
        )}
        paginationConfig={{
          currentPage: currentPage,
          totalPages: totalPages,
          onPageChange: (newPage) => setCurrentPage(newPage),
          totalItems,
        }}
      />
    </div>
  );
}